import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null, message: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, message: "Missing session id." });
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    let stopped = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", data, message: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "error", data, message: "Payment session expired." });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ phase: "pending", data, message: "Still processing — check your email for confirmation." });
          return;
        }
        if (!stopped) setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= maxAttempts) {
          setState({ phase: "error", data: null, message: "Unable to verify payment. Please contact us." });
        } else if (!stopped) {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black" />

      <Link
        to="/"
        className="absolute top-8 left-8 font-mono text-xs uppercase tracking-widest text-white/40 hover:text-neon-cyan flex items-center gap-2 transition-colors"
      >
        <ArrowLeft size={14} /> Back to site
      </Link>

      <div className="glass-strong w-full max-w-xl p-10 md:p-14 relative">
        {state.phase === "checking" && (
          <div data-testid="payment-checking" className="text-center py-8">
            <Loader2 size={48} className="text-neon-cyan mx-auto mb-6 animate-spin" />
            <h1 className="font-heading font-black text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-white/50 text-sm">One moment while we verify with Stripe.</p>
          </div>
        )}

        {state.phase === "success" && (
          <div data-testid="payment-success" className="text-center py-4">
            <CheckCircle2 size={64} className="text-neon-lime mx-auto mb-6" />
            <div className="overline mb-3">// DEPOSIT_CONFIRMED</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-3">
              You&apos;re booked in.
            </h1>
            <p className="text-white/60 text-base md:text-lg mb-2">
              Thank you for your deposit toward<br />
              <span className="text-neon-lime font-semibold">{state.data?.package_name}</span>.
            </p>
            <p className="text-white/50 text-sm max-w-md mx-auto mb-8">
              A receipt is on its way to your inbox. We&apos;ll be in touch within one business day to kick off your project and gather the materials we need to begin.
            </p>
            <Link to="/" className="btn-primary">Return Home</Link>
          </div>
        )}

        {state.phase === "pending" && (
          <div className="text-center py-4">
            <Loader2 size={48} className="text-neon-orange mx-auto mb-6 animate-spin" />
            <h1 className="font-heading font-black text-2xl mb-2">Still processing</h1>
            <p className="text-white/60 text-sm max-w-md mx-auto">{state.message}</p>
            <Link to="/" className="btn-ghost mt-8">Return Home</Link>
          </div>
        )}

        {state.phase === "error" && (
          <div className="text-center py-4">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl mb-2">We hit a snag</h1>
            <p className="text-white/60 text-sm max-w-md mx-auto mb-2">{state.message}</p>
            <p className="text-white/40 text-sm">No charges have been confirmed. Send us a note and we&apos;ll sort it out.</p>
            <Link to="/" className="btn-primary mt-8">Return Home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
